import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Cloud, Cube, ArrowRight, WindowsLogo, Martini, Globe, ChartLineUp, Broadcast, Users, Hammer, Desktop, Browser, TelegramLogo, Lightning } from '@phosphor-icons/react';
import Logo from '../components/Logo';

const Launchpad = () => {
  const navigate = useNavigate();
  const [live, setLive] = useState([]);
  const [liveLoaded, setLiveLoaded] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const firstLoad = useRef(true);

  useEffect(() => {
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const res = await axios.get('/api/analytics/live', { withCredentials: true });
        if (cancelled) return;
        setLive(res.data);
        setUpdatedAt(new Date());
      } catch (e) { /* keep last known */ }
      finally {
        if (!cancelled && firstLoad.current) { setLiveLoaded(true); firstLoad.current = false; }
      }
    };
    fetchLive();
    const iv = setInterval(fetchLive, 5000); // real-time refresh
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const totalLive = live.reduce((a, p) => a + (p.active || 0), 0);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 animate-in fade-in zoom-in-95 duration-500">
      
      <div className="mb-16 text-center flex flex-col items-center mt-12">
        <Logo className="w-24 h-24 text-white mb-6" themeColor="#00E5FF" />
        <h1 className="text-5xl font-black mb-4 tracking-tighter text-white">
          CDN<span className="text-primary">DEPLOY</span>
        </h1>
        <p className="text-slate-400 font-mono text-sm uppercase tracking-widest">Select your deployment target platform</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 w-full max-w-5xl">
        
        {/* DigitalOcean Card */}
        <div 
          onClick={() => navigate('/do')}
          className="group cursor-pointer bg-card border border-white/10 p-8 hover:bg-[#1C1F26] hover:border-primary transition-all flex flex-col relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-1 h-full bg-primary transform -translate-x-full group-hover:translate-x-0 transition-transform" />
          <Cloud size={48} className="text-primary mb-6" />
          <h2 className="text-3xl font-black mb-2">DIGITALOCEAN</h2>
          <h3 className="text-xl font-bold text-slate-300 mb-4">SPACES DEPLOYER</h3>
          <p className="text-slate-500 text-sm font-mono mb-8 flex-1">
            Batch deploy static websites directly to DigitalOcean Spaces. Manage buckets, CDN, and files.
          </p>
          <div className="flex items-center text-primary font-bold text-sm tracking-wider uppercase">
            Launch Workspace <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

        {/* Heroku Card */}
        <div 
          onClick={() => navigate('/heroku')}
          className="group cursor-pointer bg-card border border-white/10 p-8 hover:bg-[#1C1F26] hover:border-[#8b60ed] transition-all flex flex-col relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-1 h-full bg-[#8b60ed] transform -translate-x-full group-hover:translate-x-0 transition-transform" />
          <Cube size={48} className="text-[#8b60ed] mb-6" />
          <h2 className="text-3xl font-black mb-2">HEROKU</h2>
          <h3 className="text-xl font-bold text-slate-300 mb-4">APP DEPLOYER</h3>
          <p className="text-slate-500 text-sm font-mono mb-8 flex-1">
            Deploy and manage dynamic applications via Heroku. Handle builds, environment variables, and dynos.
          </p>
          <div className="flex items-center text-[#8b60ed] font-bold text-sm tracking-wider uppercase">
            Launch Workspace <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

        {/* Azure Card */}
        <div 
          onClick={() => navigate('/azure')}
          className="group cursor-pointer bg-card border border-white/10 p-8 hover:bg-[#1C1F26] hover:border-[#0078D4] transition-all flex flex-col relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-1 h-full bg-[#0078D4] transform -translate-x-full group-hover:translate-x-0 transition-transform" />
          <WindowsLogo size={48} className="text-[#0078D4] mb-6" />
          <h2 className="text-3xl font-black mb-2">AZURE</h2>
          <h3 className="text-xl font-bold text-slate-300 mb-4">WEB APP DEPLOYER</h3>
          <p className="text-slate-500 text-sm font-mono mb-8 flex-1">
            Deploy, scale, and manage Microsoft Azure Web Apps and resources directly from the dashboard.
          </p>
          <div className="flex items-center text-[#0078D4] font-bold text-sm tracking-wider uppercase">
            Launch Workspace <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

        {/* Namecheap Pro Card */}
        <div 
          onClick={() => navigate('/namecheap')}
          className="group cursor-pointer bg-card border border-white/10 p-8 hover:bg-[#1C1F26] hover:border-[#00E5FF] transition-all flex flex-col relative overflow-hidden md:col-span-2 lg:col-span-1"
        >
          <div className="absolute top-0 left-0 w-1 h-full bg-[#00E5FF] transform -translate-x-full group-hover:translate-x-0 transition-transform" />
          <Globe size={48} className="text-[#00E5FF] mb-6" />
          <h2 className="text-3xl font-black mb-2">DOMAINS</h2>
          <h3 className="text-xl font-bold text-slate-300 mb-4">NAMECHEAP PRO</h3>
          <p className="text-slate-500 text-sm font-mono mb-8 flex-1">
            End-to-End DNS Automation. Link Namecheap custom domains straight to Heroku via Git URLs.
          </p>
          <div className="flex items-center text-[#00E5FF] font-bold text-sm tracking-wider uppercase">
            Launch Workspace <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

        {/* OnForge Card */}
        <div 
          onClick={() => navigate('/forge')}
          data-testid="forge-launch-card"
          className="group cursor-pointer bg-card border border-white/10 p-8 hover:bg-[#1C1F26] hover:border-[#FB503B] transition-all flex flex-col relative overflow-hidden md:col-span-2 lg:col-span-1"
        >
          <div className="absolute top-0 left-0 w-1 h-full bg-[#FB503B] transform -translate-x-full group-hover:translate-x-0 transition-transform" />
          <Hammer size={48} weight="fill" className="text-[#FB503B] mb-6" />
          <h2 className="text-3xl font-black mb-2">ONFORGE</h2>
          <h3 className="text-xl font-bold text-slate-300 mb-4">LARAVEL DEPLOYER</h3>
          <p className="text-slate-500 text-sm font-mono mb-8 flex-1">
            Spin up Laravel sites on your Forge servers with free on-forge.com URLs. Git or zip deploys, in batches.
          </p>
          <div className="flex items-center text-[#FB503B] font-bold text-sm tracking-wider uppercase">
            Launch Workspace <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

        {/* RDP Card */}
        <div 
          onClick={() => navigate('/rdp')}
          data-testid="rdp-launch-card"
          className="group cursor-pointer bg-card border border-white/10 p-8 hover:bg-[#1C1F26] hover:border-[#14B8A6] transition-all flex flex-col relative overflow-hidden md:col-span-2 lg:col-span-1"
        >
          <div className="absolute top-0 left-0 w-1 h-full bg-[#14B8A6] transform -translate-x-full group-hover:translate-x-0 transition-transform" />
          <Desktop size={48} weight="fill" className="text-[#14B8A6] mb-6" />
          <h2 className="text-3xl font-black mb-2">RDP</h2>
          <h3 className="text-xl font-bold text-slate-300 mb-4">WINDOWS VM DEPLOYER</h3>
          <p className="text-slate-500 text-sm font-mono mb-8 flex-1">
            Provision Windows Server RDP machines on Azure. Pick CPU/RAM, create users, start/stop to save cost.
          </p>
          <div className="flex items-center text-[#14B8A6] font-bold text-sm tracking-wider uppercase">
            Launch Workspace <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

        {/* Vultr RDP Card */}
        <div 
          onClick={() => navigate('/vultr/machines')}
          data-testid="vultr-launch-card"
          className="group cursor-pointer bg-card border border-white/10 p-8 hover:bg-[#1C1F26] hover:border-[#007BFC] transition-all flex flex-col relative overflow-hidden md:col-span-2 lg:col-span-1"
        >
          <div className="absolute top-0 left-0 w-1 h-full bg-[#007BFC] transform -translate-x-full group-hover:translate-x-0 transition-transform" />
          <Cloud size={48} weight="fill" className="text-[#007BFC] mb-6" />
          <h2 className="text-3xl font-black mb-2">VULTR RDP</h2>
          <h3 className="text-xl font-bold text-slate-300 mb-4">WINDOWS ON VULTR</h3>
          <p className="text-slate-500 text-sm font-mono mb-8 flex-1">
            Provision Windows RDP on Vultr — pick RAM/vCPU, EU regions, multi-user, no subscription gating.
          </p>
          <div className="flex items-center text-[#007BFC] font-bold text-sm tracking-wider uppercase">
            Launch Workspace <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

        {/* WordPress AI Builder Card */}
        <div
          onClick={() => navigate('/wordpress/sites')}
          data-testid="wordpress-launch-card"
          className="group cursor-pointer bg-card border border-white/10 p-8 hover:bg-[#1C1F26] hover:border-[#00A0D2] transition-all flex flex-col relative overflow-hidden md:col-span-2 lg:col-span-1"
        >
          <div className="absolute top-0 left-0 w-1 h-full bg-[#00A0D2] transform -translate-x-full group-hover:translate-x-0 transition-transform" />
          <Browser size={48} weight="fill" className="text-[#00A0D2] mb-6" />
          <h2 className="text-3xl font-black mb-2">WORDPRESS AI</h2>
          <h3 className="text-xl font-bold text-slate-300 mb-4">DOMAIN → DNS → AI SITE</h3>
          <p className="text-slate-500 text-sm font-mono mb-8 flex-1">
            Pick a Namecheap domain, auto-configure Cloudflare DNS, install WordPress on cPanel and let AI build a fast, lightweight site.
          </p>
          <div className="flex items-center text-[#00A0D2] font-bold text-sm tracking-wider uppercase">
            Launch Workspace <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

        {/* DC Telegram Bot Runner Card */}
        <div
          onClick={() => navigate('/dcbot')}
          data-testid="dcbot-launch-card"
          className="group cursor-pointer bg-card border border-white/10 p-8 hover:bg-[#1C1F26] hover:border-[#229ED9] transition-all flex flex-col relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-1 h-full bg-[#229ED9] transform -translate-x-full group-hover:translate-x-0 transition-transform" />
          <TelegramLogo size={48} weight="fill" className="text-[#229ED9] mb-6" />
          <h2 className="text-3xl font-black mb-2">TELEGRAM BOT</h2>
          <h3 className="text-xl font-bold text-slate-300 mb-4">ALWAYS-ON AI RUNNER</h3>
          <p className="text-slate-500 text-sm font-mono mb-8 flex-1">
            Deploy & manage an always-on AI Telegram support bot on DigitalOcean. Edit its knowledge, watch live logs, start/stop/restart — no redeploy.
          </p>
          <div className="flex items-center text-[#229ED9] font-bold text-sm tracking-wider uppercase">
            Launch Workspace <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

        {/* AWS Amplify Card */}
        <div
          onClick={() => navigate('/amplify')}
          data-testid="amplify-launch-card"
          className="group cursor-pointer bg-card border border-white/10 p-8 hover:bg-[#1C1F26] hover:border-[#FF9900] transition-all flex flex-col relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-1 h-full bg-[#FF9900] transform -translate-x-full group-hover:translate-x-0 transition-transform" />
          <Lightning size={48} weight="fill" className="text-[#FF9900] mb-6" />
          <h2 className="text-3xl font-black mb-2">AWS AMPLIFY</h2>
          <h3 className="text-xl font-bold text-slate-300 mb-4">STATIC → AMPLIFYAPP.COM</h3>
          <p className="text-slate-500 text-sm font-mono mb-8 flex-1">
            Deploy static sites to *.amplifyapp.com via the AWS API. Bulk override re-uploads and mass wipe, just like the DO deployer (5 apps/account).
          </p>
          <div className="flex items-center text-[#FF9900] font-bold text-sm tracking-wider uppercase">
            Launch Workspace <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

      </div>
      <div
        onClick={() => navigate('/analytics')}
        data-testid="analytics-launch-card"
        className="group cursor-pointer bg-card border border-white/10 p-6 hover:bg-[#1C1F26] hover:border-[#39FF14] transition-all flex items-center gap-6 relative overflow-hidden w-full max-w-5xl mt-8"
      >
        <div className="absolute top-0 left-0 w-1 h-full bg-[#39FF14] transform -translate-x-full group-hover:translate-x-0 transition-transform" />
        <ChartLineUp size={40} className="text-[#39FF14] shrink-0" weight="bold" />
        <div className="flex-1">
          <h2 className="text-2xl font-black mb-1">ANALYTICS</h2>
          <p className="text-slate-500 text-sm font-mono">
            Live traffic, visitors, and engagement across your deployed sites. Privacy-first, powered by Umami.
          </p>
        </div>
        <div className="flex items-center text-[#39FF14] font-bold text-sm tracking-wider uppercase shrink-0">
          View Dashboard <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>

      {/* Live Visitors — all profiles line by line, real-time */}
      <div className="w-full max-w-5xl mt-8 bg-card border border-white/10" data-testid="home-live-visitors">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Broadcast size={22} className="text-[#39FF14] animate-pulse" weight="fill" />
            <div>
              <h2 className="text-lg font-black tracking-tight text-white">LIVE VISITORS</h2>
              <p className="text-[11px] font-mono text-slate-500">Real-time · updates every 5s · Umami active (last 5 min)</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-black text-[#39FF14] leading-none" data-testid="home-live-total">{totalLive}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">online now</div>
          </div>
        </div>

        <div className="divide-y divide-white/5 max-h-[420px] overflow-y-auto">
          {!liveLoaded ? (
            <div className="px-6 py-8 text-center text-slate-500 font-mono text-sm">Loading profiles…</div>
          ) : live.length === 0 ? (
            <div className="px-6 py-8 text-center text-slate-500 font-mono text-sm">No profiles found in your Umami account.</div>
          ) : (
            live.map(p => (
              <div
                key={p.id}
                data-testid={`home-profile-${p.id}`}
                onClick={() => navigate('/analytics')}
                className="flex items-center justify-between px-6 py-3 hover:bg-white/[0.03] cursor-pointer transition-colors group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${p.active > 0 ? 'bg-[#39FF14] animate-pulse' : 'bg-slate-600'}`} />
                  <Globe size={16} className="text-slate-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-white font-bold truncate group-hover:text-[#39FF14] transition-colors">{p.name || p.domain}</div>
                    <div className="text-[11px] font-mono text-slate-500 truncate">{p.domain}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Users size={14} className={p.active > 0 ? 'text-[#39FF14]' : 'text-slate-600'} weight="bold" />
                  <span className={`font-mono font-bold text-sm ${p.active > 0 ? 'text-[#39FF14]' : 'text-slate-500'}`} data-testid={`home-profile-active-${p.id}`}>{p.active}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Launchpad;